import { ChevronDown, ChevronRight, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useServices } from "@/domains/menu/services/hooks/use-services";
import type { Service } from "@/gql/graphql";
import { useSalonStore } from "@/lib/store/salon";
import { useWorkerServiceRestrictions } from "../hooks";
import type { TeamMember } from "../types";
import { getTeamMemberDisplayName } from "../utils/team-member-utils";

interface WorkerServiceRestrictionDialogProps {
  member: TeamMember;
  isOpen: boolean;
  onClose: () => void;
}

export function WorkerServiceRestrictionDialog({
  member,
  isOpen,
  onClose,
}: WorkerServiceRestrictionDialogProps) {
  const { t } = useTranslation("settings");
  const displayName = getTeamMemberDisplayName(member);
  const currentSalon = useSalonStore((state) => state.currentSalon);

  // Get all services
  const { categories, services } = useServices({ salonId: currentSalon?.id });

  // Service restriction hook
  const {
    allowedServices,
    loading: loadingAllowed,
    updating,
    updateRestrictions,
    removeRestrictions,
  } = useWorkerServiceRestrictions(member.id);

  // Local state for selected services and categories
  const [selectedServiceIds, setSelectedServiceIds] = useState<Set<string>>(new Set());
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<Set<string>>(new Set());

  // State for expanded categories
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  // Search state
  const [searchTerm, setSearchTerm] = useState("");

  // Initialize state when dialog opens or data loads
  useEffect(() => {
    if (isOpen && allowedServices && services) {
      const allServiceIds = new Set(services.map((s) => s.id));
      const allowedServiceIds = new Set(allowedServices.map((s: Service) => s.id));

      // If allowed services equals all services, then it's ALLOW_ALL mode - select all
      const hasRestrictions = allowedServiceIds.size < allServiceIds.size;

      if (!hasRestrictions) {
        // ALLOW_ALL mode - select everything
        setSelectedServiceIds(allServiceIds);
        setSelectedCategoryIds(new Set(categories.map((c) => c.id)));
      } else {
        // ALLOW_SPECIFIC mode - select only allowed
        setSelectedServiceIds(allowedServiceIds);

        // Determine selected categories (all services in category are selected)
        const selectedCats = new Set<string>();
        categories.forEach((cat) => {
          const catServices = services.filter((s) => s.categoryId === cat.id);
          const allSelected = catServices.every((s) => allowedServiceIds.has(s.id));
          if (allSelected && catServices.length > 0) {
            selectedCats.add(cat.id);
          }
        });
        setSelectedCategoryIds(selectedCats);
      }

      // Expand all categories by default
      setExpandedCategories(new Set(categories.map((c) => c.id)));
    }
  }, [isOpen, allowedServices, services, categories]);

  // Filter categories and services by search term
  const filteredData = useMemo(() => {
    if (!searchTerm) {
      return categories.map((category) => ({
        category,
        services: services.filter((s) => s.categoryId === category.id),
      }));
    }

    const term = searchTerm.toLowerCase();
    return categories
      .map((category) => {
        const categoryMatches = category.name.toLowerCase().includes(term);
        const matchingServices = services.filter(
          (s) =>
            s.categoryId === category.id && (categoryMatches || s.name.toLowerCase().includes(term))
        );
        return { category, services: matchingServices };
      })
      .filter((item) => item.services.length > 0);
  }, [categories, services, searchTerm]);

  // Toggle category expansion
  const toggleCategoryExpansion = (categoryId: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  // Handle category toggle
  const handleCategoryToggle = (categoryId: string, checked: boolean) => {
    const newSelectedCats = new Set(selectedCategoryIds);
    const newSelectedServices = new Set(selectedServiceIds);

    if (checked) {
      newSelectedCats.add(categoryId);
      // Add all services in this category
      services
        .filter((s) => s.categoryId === categoryId)
        .forEach((s) => {
          newSelectedServices.add(s.id);
        });
    } else {
      newSelectedCats.delete(categoryId);
      // Remove all services in this category
      services
        .filter((s) => s.categoryId === categoryId)
        .forEach((s) => {
          newSelectedServices.delete(s.id);
        });
    }

    setSelectedCategoryIds(newSelectedCats);
    setSelectedServiceIds(newSelectedServices);
  };

  // Handle service toggle
  const handleServiceToggle = (serviceId: string, categoryId: string, checked: boolean) => {
    const newSelectedServices = new Set(selectedServiceIds);
    const newSelectedCats = new Set(selectedCategoryIds);

    if (checked) {
      newSelectedServices.add(serviceId);

      // Check if all services in category are now selected
      const catServices = services.filter((s) => s.categoryId === categoryId);
      const allSelected = catServices.every((s) => newSelectedServices.has(s.id));
      if (allSelected) {
        newSelectedCats.add(categoryId);
      }
    } else {
      newSelectedServices.delete(serviceId);
      // Uncheck category if it was checked
      newSelectedCats.delete(categoryId);
    }

    setSelectedServiceIds(newSelectedServices);
    setSelectedCategoryIds(newSelectedCats);
  };

  // Handle save with smart logic
  const handleSave = async () => {
    // Smart logic based on selection:
    // 1. If all services selected -> remove restrictions (ALLOW_ALL mode)
    // 2. If all services in a category selected -> send category ID only
    // 3. Otherwise -> send specific service IDs

    const allServiceIds = services.map((s) => s.id);
    const allSelected = allServiceIds.every((id) => selectedServiceIds.has(id));

    if (allSelected) {
      // All services selected -> remove restrictions (ALLOW_ALL)
      await removeRestrictions(member.id);
    } else {
      // Send category IDs for fully selected categories
      // Send individual service IDs for partially selected categories
      const categoriesToSend: string[] = [];
      const servicesToSend: string[] = [];

      categories.forEach((category) => {
        const categoryServices = services.filter((s) => s.categoryId === category.id);
        const allCategoryServicesSelected = categoryServices.every((s) =>
          selectedServiceIds.has(s.id)
        );

        if (allCategoryServicesSelected && categoryServices.length > 0) {
          // All services in this category are selected -> send category ID
          categoriesToSend.push(category.id);
        } else {
          // Partially selected -> send individual service IDs
          categoryServices.forEach((s) => {
            if (selectedServiceIds.has(s.id)) {
              servicesToSend.push(s.id);
            }
          });
        }
      });

      await updateRestrictions({
        workerSalonId: member.id,
        serviceIds: servicesToSend,
        categoryIds: categoriesToSend,
      });
    }
    onClose();
  };

  // Calculate selection summary
  const getSelectionSummary = () => {
    const selectedCount = selectedServiceIds.size;
    const totalCount = services.length;

    if (selectedCount === totalCount) {
      return t("teamManagement.serviceRestrictions.canPerformAll");
    }

    return `${selectedCount} / ${totalCount} ${t("teamManagement.serviceRestrictions.services")}`;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {t("teamManagement.serviceRestrictions.title", { name: displayName })}
          </DialogTitle>
          <DialogDescription>
            {t("teamManagement.serviceRestrictions.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder={t("teamManagement.serviceRestrictions.searchPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Hierarchical List */}
          <div className="flex-1 overflow-auto border rounded-md">
            <div className="space-y-1 p-4">
              {filteredData.map(({ category, services: categoryServices }) => {
                const isExpanded = expandedCategories.has(category.id);
                const isCategorySelected = selectedCategoryIds.has(category.id);
                const isIndeterminate =
                  !isCategorySelected && categoryServices.some((s) => selectedServiceIds.has(s.id));

                return (
                  <div key={category.id} className="space-y-1">
                    {/* Category Row */}
                    <div className="flex items-center space-x-2 py-2 hover:bg-muted/50 rounded-md px-2">
                      <button
                        type="button"
                        onClick={() => toggleCategoryExpansion(category.id)}
                        className="p-0.5 hover:bg-muted rounded"
                      >
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </button>
                      <Checkbox
                        id={`cat-${category.id}`}
                        checked={isIndeterminate ? "indeterminate" : isCategorySelected}
                        onCheckedChange={(checked) =>
                          handleCategoryToggle(category.id, checked as boolean)
                        }
                      />
                      <Label
                        htmlFor={`cat-${category.id}`}
                        className="flex-1 cursor-pointer font-medium"
                      >
                        {category.name}
                        <span className="text-sm text-muted-foreground ml-2">
                          ({categoryServices.length})
                        </span>
                      </Label>
                    </div>

                    {/* Services under category */}
                    {isExpanded && (
                      <div className="ml-6 space-y-1">
                        {categoryServices.map((service: Service) => (
                          <div
                            key={service.id}
                            className="flex items-center space-x-2 py-1.5 hover:bg-muted/50 rounded-md px-2"
                          >
                            <div className="w-4" /> {/* Spacer for alignment */}
                            <Checkbox
                              id={`service-${service.id}`}
                              checked={selectedServiceIds.has(service.id)}
                              onCheckedChange={(checked) =>
                                handleServiceToggle(
                                  service.id,
                                  service.categoryId,
                                  checked as boolean
                                )
                              }
                            />
                            <Label
                              htmlFor={`service-${service.id}`}
                              className="flex-1 cursor-pointer text-sm"
                            >
                              {service.name}
                            </Label>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Selection Summary */}
          <div className="text-sm text-muted-foreground border-t pt-3">{getSelectionSummary()}</div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={updating}>
            {t("actions.cancel")}
          </Button>
          <Button onClick={handleSave} disabled={updating || loadingAllowed}>
            {updating ? t("actions.saving") : t("actions.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
